/**
 * Applying one change to many actions, and the special case of a shift
 * handover.
 *
 * Bulk writes are the same read-modify-write as a single cell: each row is
 * patched independently so a failure on one does not invent a rollback of the
 * ones that already succeeded. That is also why the result is a list of
 * failures rather than a thrown error — the operator needs to see which tasks
 * did not move.
 */

import type { ActionAssigneeKind } from '../ICSConstants';
import {
  assigneePatch,
  resolveActionAssignee,
  type AssigneeDirectory,
} from './actionEditModel';
import type { ActionEntity } from '../hooks/useScopedActionForm';

export type BulkFailure = {
  actionId: string;
  name: string;
  message: string;
};

export type BulkResult = {
  attempted: number;
  succeeded: number;
  failures: BulkFailure[];
};

export function describeBulkResult(result: BulkResult): string {
  if (result.attempted === 0) return 'Nothing was selected.';
  if (result.failures.length === 0) {
    return result.succeeded === 1
      ? 'Updated 1 task.'
      : `Updated ${result.succeeded} tasks.`;
  }
  const names = result.failures.map((failure) => failure.name).join(', ');
  return `${result.succeeded} of ${result.attempted} updated. Failed: ${names}.`;
}

/**
 * Position assignments currently held, which is the set a handover can move.
 *
 * A position with no open work is omitted: offering it would be a button that
 * does nothing.
 */
export function handoverPositions(
  actions: ActionEntity[],
  directory: AssigneeDirectory
): { id: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  actions.forEach((action) => {
    if (action.archived || action.status === 'Closed') return;
    const resolved = resolveActionAssignee(action, directory);
    if (resolved.kind !== 'position' || !resolved.id) return;
    counts.set(resolved.id, (counts.get(resolved.id) ?? 0) + 1);
  });

  return directory.positions
    .filter((position) => counts.has(position.value))
    .map((position) => ({
      id: position.value,
      label: position.label,
      count: counts.get(position.value) ?? 0,
    }));
}

/** The people a position's work can be handed to. */
export function handoverPeople(
  directory: AssigneeDirectory
): { id: string; label: string }[] {
  return directory.users.map((user) => ({ id: user.value, label: user.label }));
}

/**
 * Open tasks currently assigned to this position.
 *
 * Closed work is left alone: a finished task is history, not something the
 * incoming person inherits.
 */
export function actionsForHandover(
  actions: ActionEntity[],
  positionId: string
): ActionEntity[] {
  return actions.filter((action) => {
    if (action.archived || action.status === 'Closed') return false;
    return (
      action.assignee_kind === 'position' && action.assignee_id === positionId
    );
  });
}

export function parseAssigneeToken(
  token: string
): { kind: ActionAssigneeKind; id: string } | null {
  const separator = token.indexOf(':');
  if (separator <= 0) return null;
  const kind = token.slice(0, separator) as ActionAssigneeKind;
  const id = token.slice(separator + 1);
  if (!id || (kind !== 'user' && kind !== 'team' && kind !== 'position')) {
    return null;
  }
  return { kind, id };
}

/** The patch a palette assign command encodes in `__assignee`. */
export function patchFromPalette(
  patch: Record<string, unknown>
): Record<string, unknown> {
  const token = patch.__assignee;
  if (typeof token !== 'string') return patch;
  const parsed = parseAssigneeToken(token);
  if (!parsed) return {};
  return assigneePatch(parsed.kind, parsed.id);
}

export function userAssignmentPatch(userId: string): Record<string, unknown> {
  return assigneePatch('user', userId);
}
